import { StudyConditionsType, StudyOptionsType } from "@/models";
import { RadioOption } from "./components/SearchForm/components/RadioGroupCard/RadioGroupCard";

import arthritisImg from "@/assets/images/disease-grid/arthritis-256x256.webp";
import bronchitisImg from "@/assets/images/disease-grid/bronchitis-256x256.webp";
import cancerImg from "@/assets/images/disease-grid/cancer-256x256.webp";
import diabetesImg from "@/assets/images/disease-grid/diabetes-512x512.webp";
import eyeImg from "@/assets/images/disease-grid/eye-256x256.webp";
import breastImg from "@/assets/images/disease-grid/breast-256x256.webp";
import heartImg from "@/assets/images/disease-grid/heart-256x256.webp";
import prostateCancerImg from "@/assets/images/disease-grid/prostate-cancer-256x256.webp";
import stomachCancerImg from "@/assets/images/disease-grid/stomach-cancer-256x256.webp";
import lungCancerImg from "@/assets/images/disease-grid/lung-cancer-256x256.webp";
import immuneSystemImg from "@/assets/images/disease-grid/immune-system-256x256.webp";
import mentalHealthImg from "@/assets/images/disease-grid/mental-health-256x256.webp";
import neurologicalImg from "@/assets/images/disease-grid/neurological-256x256.webp";
import painConditionsImg from "@/assets/images/disease-grid/pain-conditions-256x256.webp";
import skinConditionsImg from "@/assets/images/disease-grid/skin-conditions-256x256.webp";
import stomachImg from "@/assets/images/disease-grid/stomach-256x256.webp";
import reproductiveHealthImg from "@/assets/images/ecrf/reproductive_health.webp";

/**
 * StudyOptions is a list of study options that can be filtered in the search screen.
 */
export const StudyOptions: StudyOptionsType[] = [
    {
        // Drug studies with previous patient experience means phase 3 and 4
        //      drug studies that have previous patient experience
        // See server/src/controller/search.ts
        title: "DRUG STUDIES THAT HAVE PREVIOUS PATIENT EXPERIENCE",
        slug: "previous-experience",
    },
    {
        // Drug studies with some patient experience means phase 1 and 2
        //      drug studies that have some patient experience
        // See server/src/controller/search.ts
        title: "DRUG STUDIES THAT HAVE SOME PATIENT EXPERIENCE",
        slug: "some-experience",
    },
    {
        // Studies with devices
        // See server/src/controller/search.ts
        title: "STUDIES WITH DEVICES",
        slug: "with-devices",
    },
    {
        // Observational studies with no drugs or devices
        // See server/src/controller/search.ts
        title: "OBSERVATIONAL STUDIES WITH NO DRUGS OR DEVICES",
        slug: "observe-only",
    },
];

/**
 * FIXME: Conditions is shown ??
 */
export const Conditions: StudyConditionsType[] = [
    {
        title: "Arthritis",
        slug: "Arthritis",
        image: arthritisImg,
        conditions: ["Arthritis"],
    },
    {
        title: "Bronchitis",
        slug: "Bronchitis",
        image: bronchitisImg,
        conditions: ["Bronchitis"],
    },
    {
        title: "Cancer",
        slug: "Cancer",
        image: cancerImg,
        conditions: ["Cancer"],
    },
    {
        title: "Diabetes",
        slug: "Diabetes",
        image: diabetesImg,
        conditions: ["Diabetes"],
    },
    {
        title: "Eye",
        slug: "Eye",
        image: eyeImg,
        conditions: ["Eye"],
    },
    {
        title: "Heart Conditions",
        slug: "Heart",
        image: heartImg,
        conditions: ["Heart Conditions"],
    },
    {
        title: "Immune Systems",
        slug: "Immune",
        image: immuneSystemImg,
        conditions: ["Immune Systems"],
    },
    {
        title: "Mental Health",
        slug: "Mental Health",
        image: mentalHealthImg,
        conditions: ["Mental Health"],
    },
    {
        title: "Neurological",
        slug: "Nerve",
        image: neurologicalImg,
        conditions: ["Neurological"],
    },
    {
        title: "Pain Conditions",
        slug: "Pain",
        image: painConditionsImg,
        conditions: ["Pain Conditions"],
    },
    {
        title: "Skin Conditions",
        slug: "Skin",
        image: skinConditionsImg,
        conditions: ["Skin Conditions"],
    },
    {
        title: "Stomach & Intestine",
        slug: "Stomach",
        image: stomachImg,
        conditions: ["Stomach & Intestine"],
    },
];

export const EcrfConditions: RadioOption[] = [
    {
        label: (
            <span>
                Endometrial/
                <wbr />
                Uterine Cancer
            </span>
        ),
        value: "ecrf",
        imageSrc: reproductiveHealthImg,
    },
    {
        label: "All Other Conditions",
        value: "all",
        imageSrc: immuneSystemImg,
    },
];

export const NavTrialsConditions: StudyConditionsType[] = [
    {
        title: "Breast Cancer",
        slug: "Breast Cancer",
        image: breastImg,
        conditions: ["Breast Cancer"],
    },

    {
        title: "Prostate Cancer",
        slug: "Prostate Cancer",
        image: prostateCancerImg,
        conditions: ["Prostate Cancer"],
    },

    {
        title: "Lung Cancer",
        slug: "Lung Cancer",
        image: lungCancerImg,
        conditions: ["Lung Cancer"],
    },
    {
        title: "Colorectal Cancer",
        slug: "Colorectal Cancer",
        image: stomachCancerImg,
        conditions: ["Colorectal Cancer"],
    },
    {
        title: "Diabetes",
        slug: "Diabetes",
        image: diabetesImg,
        conditions: ["Diabetes"],
    },
    {
        title: "Cardiovascular Disease",
        slug: "Cardiovascular Disease",
        image: heartImg,
        conditions: ["Cardiovascular Disease"],
    },
    {
        title: "All Other Conditions",
        slug: "all",
        image: immuneSystemImg,
        conditions: [],
    },
];

/**
 * FIXME: OverviewData is shown ??
 */
export const OverviewData = [
    {
        title: "What is a Clinical Trial?",
        description:
            "A clinical study involves research using human volunteers (also called participants) that is intended to add to medical knowledge. There are two main types of clinical studies: clinical trials (also called interventional studies) and observational studies. \n\n  In a clinical trial, participants receive specific interventions according to the research plan or protocol created by the investigators. These interventions may be medical products, such as drugs or devices; procedures; or changes to participants' behavior, such as diet. Clinical trials may compare a new medical approach to a standard one that is already available, to a placebo that contains no active ingredients, or to no intervention. Some clinical trials compare interventions that are already available to each other. When a new product or approach is being studied, it is not usually known whether it will be helpful, harmful, or no different than available alternatives (including no intervention). The investigators try to determine the safety and efficacy of the intervention by measuring certain outcomes in the participants. For example, investigators may give a drug or treatment to participants who have high blood pressure to see whether their blood pressure decreases.",
        key: "item_1",
    },
    {
        title: "Who Can Participate in a Clinical Study?",
        description:
            "Clinical studies have standards outlining who can participate. These standards are called eligibility criteria and are listed in the protocol. Some research studies seek participants who have the illnesses or conditions that will be studied, other studies are looking for healthy participants, and some studies are limited to a predetermined group of people who are asked by researchers to enroll. \n\nEligibility. The factors that allow someone to participate in a clinical study are called inclusion criteria, and the factors that disqualify someone from participating are called exclusion criteria. They are based on characteristics such as age, gender, the type and stage of a disease, previous treatment history, and other medical conditions.",
        key: "item_2",
    },
    {
        title: "How are Participants Protected?",
        description:
            "Informed consent is a process used by researchers to provide potential and enrolled participants with information about a clinical study. This information helps people decide whether they want to enroll or continue to participate in the study. The informed consent process is intended to protect participants and should provide enough information for a person to understand the risks of, potential benefits of, and alternatives to the study. In general, a person must sign an informed consent document before joining a study to show that he or she was given information on the risks, potential benefits, and alternatives and that he or she understands it. Signing the document and providing consent is not a contract. Participants may withdraw from a study at any time, even if the study is not over. \n\nInstitutional review boards. Each federally supported or conducted clinical study and each study of a drug, biological product, or medical device regulated by FDA must be reviewed, approved, and monitored by an institutional review board (IRB). An IRB is made up of doctors, researchers, and members of the community. Its role is to make sure that the study is ethical and that the rights and welfare of participants are protected. This includes making sure that research risks are minimized and are reasonable in relation to any potential benefits, among other responsibilities. The IRB also reviews the informed consent document.",
        key: "item_3",
    },
    {
        title: "What are the Considerations for Participation?",
        description:
            "Participating in a clinical study contributes to medical knowledge. The results of these studies can make a difference in the care of future patients by providing information about the benefits and risks of therapeutic, preventative, or diagnostic products or interventions. Clinical trials provide the basis for the development and marketing of new drugs, biological products, and medical devices. Sometimes, the safety and the effectiveness of the experimental approach or use may not be fully known at the time of the trial. Some trials may provide participants with the prospect of receiving direct medical benefits, while others do not. Most trials involve some risk of harm or injury to the participant, although it may not be greater than the risks related to routine medical care or disease progression.",
        key: "item_4",
    },
];
